<template>
    <div class="AppNavigation" :class="{ 'is-collapsed': isCollapsed }" id="AppNavigation">
        <nav class="Wrapper">
            <template v-if="!isMobile">
                <div class="NavigationTop">
                    <button
                        class="CollapseToggle"
                        @click="toggleNavigation"
                        :class="{ 'is-collapsed': isCollapsed }"
                    >
                        <i class="ri-menu-line"></i>
                    </button>

                    <router-link v-if="!isCollapsed" to="/" class="Logo">
                        <img src="@/assets/svg/logo.svg" alt />
                    </router-link>

                    <template v-if="!isDataLoaded || !modules || modules.length === 0">
                        <div
                            v-for="i in 8"
                            :key="`nav-skeleton-${i}`"
                            class="Nav-Link Nav-Link-Skeleton"
                        >
                            <div class="SkeletonIcon"></div>
                            <div v-if="!isCollapsed" class="SkeletonText"></div>
                        </div>
                    </template>

                    <template v-else>
                        <NavLink
                            v-for="item in desktopNavItems"
                            :key="item.key"
                            :route="item.route"
                            :icon="item.icon"
                            :label="item.label"
                            :collapsed-label="item.collapsedLabel"
                            :tooltip="item.label"
                            :is-collapsed="isCollapsed"
                            @click="onNavClick"
                        />
                    </template>

                    <div
                        v-if="!hasMyPos && isDataLoaded && !isCollapsed"
                        class="mypos-promo-banner"
                    >
                        <a @click.prevent="showMyPosModal = true" href="#" class="mypos-link">
                            <span>MyPos Go2 9€</span>
                            <i class="ri-bank-card-line"></i>
                        </a>
                    </div>
                </div>

                <MyPosModal
                    :show="showMyPosModal"
                    :isLoading="false"
                    @onClose="showMyPosModal = false"
                />

                <UpgradeModal :show="showUpgradeModal" @onClose="showUpgradeModal = false" />

                <div class="NavigationBottom">
                    <div
                        v-if="
                            !isCollapsed && (availableAccounts.length > 0 || user?.customerNumber)
                        "
                        class="AccountInfo"
                    >
                        <div v-if="user?.customerNumber" class="CustomerNumberDisplay">
                            <span class="CustomerNumberLabel">Kundennummer</span>
                            <span class="CustomerNumberValue">{{ user.customerNumber }}</span>
                        </div>
                        <div v-if="availableAccounts.length > 0" class="AccountSwitcher">
                            <select
                                v-model="selectedAccountCustomerNumber"
                                @change="handleAccountSwitchChange"
                                class="AccountSelect"
                                :disabled="isSwitchingAccount"
                            >
                                <option value="" disabled>Konto wechseln</option>
                                <option
                                    v-for="account in availableAccounts"
                                    :key="account.customerNumber"
                                    :value="account.customerNumber"
                                >
                                    {{ account.businessName }} ({{ account.customerNumber }})
                                </option>
                            </select>
                        </div>
                        <div v-if="isDevMode" class="DevAccountSwitcher">
                            <input
                                v-model="devCustomerNumber"
                                @keyup.enter="handleDevAccountSwitch"
                                class="DevAccountInput"
                                type="text"
                                inputmode="numeric"
                                placeholder="Kundennummer eingeben"
                                :disabled="isSwitchingAccount"
                            />
                            <button
                                @click="handleDevAccountSwitch"
                                class="DevAccountButton"
                                :disabled="isSwitchingAccount || !devCustomerNumber"
                            >
                                <i class="ri-arrow-right-line"></i>
                            </button>
                        </div>
                    </div>
                    <MenuDropdown
                        v-if="!isCollapsed"
                        title="Menu"
                        :items="menuItems"
                        :items-at-top="true"
                        :open-upward="true"
                        class="NavigationMenuDropdown theme-dark"
                    >
                        <template #button-content="{ isOpen, openUpward }">
                            <div class="MenuTrigger-Content">
                                <div class="MenuTrigger-Info">
                                    <div class="MenuTrigger-Name">{{ userName }}</div>
                                    <div class="MenuTrigger-PlanRow">
                                        <div class="MenuTrigger-Plan">{{ planDisplayName }}</div>
                                        <button
                                            v-if="isPlanLite"
                                            class="MenuTrigger-UpgradeButton"
                                            @click.stop="showUpgradeModal = true"
                                        >
                                            Upgrade
                                        </button>
                                    </div>
                                </div>
                                <i
                                    :class="[
                                        openUpward ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line',
                                        { 'is-open': isOpen },
                                        'MenuTrigger-Arrow',
                                    ]"
                                ></i>
                            </div>
                        </template>
                    </MenuDropdown>
                </div>

                <NavigationIndicator
                    v-if="!isCollapsed"
                    :is-mobile="isMobile"
                    :is-collapsed="isCollapsed"
                    :drawer-routes="drawerRoutes"
                    ref="navIndicator"
                />
                <HoverIndicator v-if="!isCollapsed" variant="default" ref="hoverIndicator" />
            </template>

            <template v-else>
                <div class="MobileNavBar">
                    <NavLink
                        v-for="item in mobileBarItems"
                        :key="item.key"
                        :route="item.route"
                        :icon="item.icon"
                        :label="item.mobileBarLabel || item.label"
                        @click="onNavClick"
                    />
                    <button
                        class="Nav-Link More-Button"
                        :class="{
                            'router-link-exact-active': isCurrentRouteInDrawer,
                            'active-drawer-link': isCurrentRouteInDrawer,
                        }"
                        @click="openMoreDrawer"
                    >
                        <i class="ri-more-2-fill"></i>
                        <span>Mehr</span>
                    </button>
                </div>
                <MobileDrawer :is-visible="showMoreMenu" title="Mehr" @close="closeMoreDrawer">
                    <div class="DrawerUserSection">
                        <MenuDropdown
                            :title="userName"
                            :items="mobileMenuItems"
                            class="MobileDrawerMenu theme-dark"
                        >
                            <template #button-content="{ isOpen, openUpward }">
                                <div class="MobileMenuTrigger-Content">
                                    <div class="MobileMenuTrigger-Info">
                                        <div class="MobileMenuTrigger-Name">{{ userName }}</div>
                                        <div
                                            class="MobileMenuTrigger-Plan"
                                            v-if="user?.customerNumber"
                                        >
                                            Kundennummer: {{ user.customerNumber }}
                                        </div>
                                    </div>
                                    <i
                                        :class="[
                                            openUpward
                                                ? 'ri-arrow-up-s-line'
                                                : 'ri-arrow-down-s-line',
                                            { 'is-open': isOpen },
                                            'MobileMenuTrigger-Arrow',
                                        ]"
                                    ></i>
                                </div>
                            </template>
                        </MenuDropdown>
                        <div v-if="availableAccounts.length > 0" class="DrawerAccountSwitcher">
                            <select
                                v-model="selectedAccountCustomerNumber"
                                @change="handleAccountSwitchChange"
                                class="DrawerAccountSelect"
                                :disabled="isSwitchingAccount"
                            >
                                <option value="" disabled>Konto wechseln</option>
                                <option
                                    v-for="account in availableAccounts"
                                    :key="account.customerNumber"
                                    :value="account.customerNumber"
                                >
                                    {{ account.businessName }} ({{ account.customerNumber }})
                                </option>
                            </select>
                        </div>
                        <div v-if="isDevMode" class="DrawerDevAccountSwitcher">
                            <input
                                v-model="devCustomerNumber"
                                @keyup.enter="handleDevAccountSwitch"
                                class="DrawerDevAccountInput"
                                type="text"
                                inputmode="numeric"
                                placeholder="Kundennummer eingeben (Dev)"
                                :disabled="isSwitchingAccount"
                            />
                            <button
                                @click="handleDevAccountSwitch"
                                class="DrawerDevAccountButton"
                                :disabled="isSwitchingAccount || !devCustomerNumber"
                            >
                                <i class="ri-arrow-right-line"></i>
                            </button>
                        </div>
                    </div>
                    <Button
                        v-for="item in mobileDrawerItems"
                        :key="item.key"
                        variant="plain"
                        class="DrawerNavButton"
                        @onClick="navigateAndClose(item.route)"
                    >
                        <i :class="item.icon"></i>
                        {{ item.label }}
                    </Button>
                </MobileDrawer>
                <NavigationIndicator
                    :is-mobile="isMobile"
                    :is-collapsed="isCollapsed"
                    :drawer-routes="drawerRoutes"
                    ref="mobileNavIndicator"
                />
                <HoverIndicator v-if="!isCollapsed" variant="default" ref="hoverIndicator" />
            </template>
        </nav>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import AppFooter from '@/components/AppFooter.vue';
import Button from '@/components/widgets/Button.vue';
import MyPosModal from '@/components/widgets/MyPosModal.vue';
import Modal from '@/components/widgets/Modal.vue';
import MobileDrawer from '@/components/widgets/MobileDrawer.vue';
import HoverIndicator from '@/components/widgets/HoverIndicator.vue';
import NavigationIndicator from '@/components/widgets/NavigationIndicator.vue';
import NavLink from '@/components/widgets/NavLink.vue';
import UpgradeModal from '@/components/UpgradeModal.vue';
import MenuDropdown from '@/components/MenuDropdown.vue';
import { gsap } from 'gsap';
import resizeMixin from '@/mixins/resizeMixin';
import routerReloadMixin from '@/mixins/routerReloadMixin';

export default {
    name: 'AppNavigation',
    mixins: [resizeMixin, routerReloadMixin],
    components: {
        AppFooter,
        Button,
        MyPosModal,
        Modal,
        MobileDrawer,
        HoverIndicator,
        NavigationIndicator,
        NavLink,
        UpgradeModal,
        MenuDropdown,
    },
    data() {
        return {
            isLoggingOut: false,
            showMyPosModal: false,
            showUpgradeModal: false,
            showMoreMenu: false,
            wasMobile: window.innerWidth < 768,
            isCollapsed: localStorage.getItem('navigationCollapsed') === 'true',
            selectedAccountCustomerNumber: '',
            devCustomerNumber: '',
        };
    },
    watch: {
        isMobile: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (!newVal && oldVal) {
                        setTimeout(() => {
                            this.updateNavWidth();
                        }, 350);
                    } else {
                        this.updateNavWidth();
                    }
                }
            },
        },
        isDataLoaded: {
            handler(newVal) {
                if (newVal) {
                    this.$nextTick(() => {
                        if (this.$refs.hoverIndicator) {
                            this.$refs.hoverIndicator.reinitializeListeners();
                        }
                        // Trigger recalculation on the NavigationIndicator
                        if (this.$refs.navIndicator) {
                            this.$refs.navIndicator.recalculate();
                        }
                        if (this.$refs.mobileNavIndicator) {
                            this.$refs.mobileNavIndicator.recalculate();
                        }
                    });
                }
            },
            immediate: true,
        },
        availableAccounts: {
            handler(newVal) {
                // Available accounts updated
            },
            immediate: true,
        },
    },
    computed: {
        ...mapGetters([
            'businessName',
            'modules',
            'planType',
            'isPlanLite',
            'isPlanBasic',
            'isPlanUltimate',
            'hasMyPos',
            'user',
            'availableAccounts',
            'isSwitchingAccount',
            'dataStoragePlanLabel',
        ]),
        ...mapGetters('promotion', ['showPromotion', 'promotionDeadlineAbbreviated']),
        isPlanBasicOrHigher() {
            return true;
        },
        hasPerformanceAnalyticsModule() {
            return this.modules.includes('performanceAnalysis');
        },
        hasDriverLogsModule() {
            return this.modules.includes('driverLogs');
        },
        hasReminderModule() {
            return this.modules.includes('reminder');
        },
        hasSchedulerModule() {
            return this.modules.includes('scheduler');
        },
        hasCalendarModule() {
            return this.modules.includes('calendar');
        },
        hasSavingsCalculatorModule() {
            return this.modules.includes('savingsCalculator');
        },
        hasWorkTimeModule() {
            return this.modules.includes('workTime');
        },
        hasRevenueModule() {
            return this.modules.includes('revenue');
        },
        hasOperationRecordsModule() {
            return this.modules.includes('operationRecords');
        },
        hasDashboardModule() {
            return this.modules.includes('dashboard');
        },
        navigationItems() {
            return [
                {
                    key: 'dashboard',
                    route: '/dashboard',
                    icon: 'ri-dashboard-line',
                    label: 'Dashboard',
                    collapsedLabel: 'D',
                    visible: this.hasDashboardModule,
                    showInMobileBar: true,
                    mobileBarLabel: 'Home',
                },
                {
                    key: 'analytics',
                    route: '/analytics',
                    icon: 'ri-bar-chart-line',
                    label: 'Analyse',
                    collapsedLabel: 'A',
                    visible: this.hasPerformanceAnalyticsModule,
                    showInMobileDrawer: true,
                },
                {
                    key: 'schedular',
                    route: '/schedular',
                    icon: 'ri-timeline-view',
                    label: 'Schichtplanner',
                    collapsedLabel: 'S',
                    visible: this.hasSchedulerModule,
                    showInMobileDrawer: true,
                },
                {
                    key: 'calendar',
                    route: '/calendar',
                    icon: 'ri-calendar-event-line',
                    label: 'Kalender',
                    collapsedLabel: 'K',
                    visible: this.hasCalendarModule,
                    showInMobileDrawer: true,
                },
                {
                    key: 'driversLog',
                    route: '/driversLog',
                    icon: 'ri-booklet-line',
                    label: 'Fahrtenbuch',
                    collapsedLabel: 'F',
                    visible: this.hasDriverLogsModule,
                    showInMobileDrawer: true,
                },
                {
                    key: 'revenue',
                    route: '/revenue',
                    icon: 'ri-money-dollar-circle-line',
                    label: 'Umsatz',
                    collapsedLabel: 'U',
                    visible: this.hasRevenueModule,
                    showInMobileBar: true,
                },
                {
                    key: 'working-time',
                    route: '/working-time',
                    icon: 'ri-time-line',
                    label: 'Arbeitszeiten',
                    collapsedLabel: 'A',
                    visible: this.hasWorkTimeModule,
                    showInMobileDrawer: true,
                },
                {
                    key: 'shifts',
                    route: '/shifts',
                    icon: 'ri-repeat-line',
                    label: 'Schichten',
                    collapsedLabel: 'S',
                    visible: true,
                    showInMobileBar: true,
                },
                {
                    key: 'trips',
                    route: '/trips',
                    icon: 'ri-steering-2-line',
                    label: 'Einzelfahrten',
                    collapsedLabel: 'E',
                    visible: true,
                    showInMobileBar: true,
                    mobileBarLabel: 'Fahrten',
                },
                {
                    key: 'operationride',
                    route: '/operationride',
                    icon: 'ri-car-line',
                    label: 'Betriebsnachweise',
                    collapsedLabel: 'B',
                    visible: this.hasOperationRecordsModule,
                    showInMobileDrawer: true,
                },
                {
                    key: 'card-payments',
                    route: '/card-payments',
                    icon: 'ri-bank-card-line',
                    label: 'Kartenzahlungen',
                    collapsedLabel: 'K',
                    visible: this.hasMyPos,
                    showInMobileDrawer: true,
                },
                {
                    key: 'employees',
                    route: '/employees',
                    icon: 'ri-team-line',
                    label: 'Mitarbeiter',
                    collapsedLabel: 'M',
                    visible: true,
                    showInMobileBar: true,
                    mobileBarLabel: 'Fahrer',
                },
                {
                    key: 'cars',
                    route: '/cars',
                    icon: 'ri-car-fill',
                    label: 'Fahrzeuge',
                    collapsedLabel: 'F',
                    visible: true,
                    showInMobileDrawer: true,
                },
                {
                    key: 'savings-calculator',
                    route: '/savings-calculator',
                    icon: 'ri-calculator-line',
                    label: 'Rücklagenrechner',
                    collapsedLabel: 'R',
                    visible: this.hasSavingsCalculatorModule,
                    showInMobileDrawer: true,
                },
            ];
        },
        desktopNavItems() {
            return this.navigationItems.filter((item) => item.visible !== false);
        },
        mobileBarItems() {
            return this.navigationItems.filter(
                (item) => item.showInMobileBar && item.visible !== false,
            );
        },
        mobileDrawerItems() {
            return this.navigationItems.filter(
                (item) => item.showInMobileDrawer && item.visible !== false,
            );
        },
        drawerRoutes() {
            return [
                '/analytics',
                '/schedular',
                '/calendar',
                '/driversLog',
                '/working-time',
                '/operationride',
                '/card-payments',
                '/savings-calculator',
                '/invoice',
                '/cars',
                '/settings',
            ];
        },
        isCurrentRouteInDrawer() {
            if (!this.isMobile) return false;

            const currentRoute = this.$route.path;
            const drawerRoutes = [
                '/analytics',
                '/schedular',
                '/calendar',
                '/driversLog',
                '/working-time',
                '/operationride',
                '/card-payments',
                '/savings-calculator',
                '/invoice',
                '/cars',
                '/settings',
            ];

            return drawerRoutes.includes(currentRoute);
        },
        isDataLoaded() {
            return !!this.businessName;
        },
        isDevMode() {
            return (
                import.meta.env.DEV ||
                window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1'
            );
        },
        userName() {
            return this.user?.name || this.user?.email?.split('@')[0] || this.businessName || '';
        },
        planDisplayName() {
            if (this.dataStoragePlanLabel) {
                return this.dataStoragePlanLabel;
            }
            const planMap = {
                lite: 'Lite Tarif',
                basic: 'Basic Tarif',
                pro: 'Pro Tarif',
                ultimate: 'Ultimate Tarif',
                premium: 'Premium Tarif',
            };
            return planMap[this.planType] || 'Lite Tarif';
        },
        menuItems() {
            return [
                {
                    label: 'Einstellungen',
                    icon: 'ri-settings-3-line',
                    to: '/settings',
                    showArrow: false,
                },
                {
                    label: 'Neueste Updates',
                    icon: 'ri-megaphone-line',
                    to: '/changelog',
                    showArrow: false,
                },
                {
                    label: 'Rechnungen',
                    icon: 'ri-file-text-line',
                    to: '/invoice',
                    showArrow: false,
                },
                {
                    label: 'Tarif upgraden',
                    icon: 'ri-arrow-right-up-box-line',
                    action: () => {
                        this.showUpgradeModal = true;
                    },
                },
                {
                    label: 'Abmelden',
                    icon: 'ri-logout-box-line',
                    action: () => {
                        this.handleLogoutClick();
                    },
                },
            ].reverse();
        },
        mobileMenuItems() {
            return [
                {
                    label: 'Einstellungen',
                    icon: 'ri-settings-3-line',
                    to: '/settings',
                    showArrow: false,
                    action: () => {
                        this.closeMoreDrawer();
                        this.$router.push('/settings');
                    },
                },
                {
                    label: 'Neueste Updates',
                    icon: 'ri-megaphone-line',
                    to: '/changelog',
                    showArrow: false,
                    action: () => {
                        this.closeMoreDrawer();
                        this.$router.push('/changelog');
                    },
                },
                {
                    label: 'Rechnungen',
                    icon: 'ri-file-text-line',
                    to: '/invoice',
                    showArrow: false,
                    action: () => {
                        this.closeMoreDrawer();
                        this.$router.push('/invoice');
                    },
                },
                {
                    label: 'Tarif upgraden',
                    icon: 'ri-arrow-right-up-box-line',
                    action: () => {
                        this.closeMoreDrawer();
                        this.showUpgradeModal = true;
                    },
                },
                {
                    label: 'Abmelden',
                    icon: 'ri-logout-box-line',
                    action: () => {
                        this.closeMoreDrawer();
                        this.handleLogoutClick();
                    },
                },
            ].reverse();
        },
    },
    methods: {
        ...mapActions(['setUser', 'switchAccount']),

        handleResize() {
            const wasMobile = this.wasMobile;
            this.wasMobile = this.isMobile;

            if (this.isMobile !== wasMobile) {
                this.positionsCalculated = false;
                const indicator = this.$refs.indicator;
                if (indicator) {
                    if (this.isMobile) {
                        indicator.style.transform = 'translateY(0px)';
                        this.currentIndicatorPosition = { x: 0, y: 0 };
                    } else {
                        indicator.style.transform = 'translateX(0px)';
                        this.currentIndicatorPosition = { x: 0, y: 90 };
                    }
                }
            }

            const delay = this.isMobile !== wasMobile ? 350 : 100;
            setTimeout(() => {
                this.updateNavWidth();
            }, delay);

            const indicatorDelay = this.isMobile !== wasMobile ? 300 : 200;

            setTimeout(() => {
                this.$nextTick(() => {
                    // Trigger recalculation on NavigationIndicator components
                    if (this.$refs.navIndicator) {
                        this.$refs.navIndicator.recalculate();
                    }
                    if (this.$refs.mobileNavIndicator) {
                        this.$refs.mobileNavIndicator.recalculate();
                    }
                });
            }, indicatorDelay);
        },

        async handleLogoutClick(e) {
            this.isLoggingOut = true;
            await axios.get(`${import.meta.env.VITE_API_BASE_URL}/logout`, {
                withCredentials: true,
            });
            setTimeout(() => {
                sessionStorage.clear();
                localStorage.clear();
                this.$router.push('/').then(() => {
                    window.location.reload();
                });
            }, 200);
        },
        handleMyPosRedirect() {
            window.open(
                'https://www.mypos.eu/de/enroll/ref:1007477',
                '_blank',
                'noopener,noreferrer',
            );
            this.showMyPosModal = false;
        },
        async handleAccountSwitchChange(event) {
            const customerNumber = event.target.value;
            if (!customerNumber) return;

            if (Number(customerNumber) === this.user?.customerNumber) {
                this.selectedAccountCustomerNumber = '';
                return;
            }

            try {
                const success = await this.switchAccount(Number(customerNumber));

                if (success) {
                    this.$toasted?.show('Konto erfolgreich gewechselt', {
                        type: 'success',
                    });
                    this.selectedAccountCustomerNumber = '';
                    setTimeout(() => {
                        this.$router.go(0);
                    }, 300);
                } else {
                    this.selectedAccountCustomerNumber = '';
                }
            } catch (error) {
                this.selectedAccountCustomerNumber = '';
                if (error.response) {
                    if (error.response.status === 401) {
                        this.$toasted?.show('Authentifizierung erforderlich', {
                            type: 'error',
                        });
                    } else if (error.response.status === 500) {
                        this.$toasted?.show('Serverfehler beim Wechseln des Kontos', {
                            type: 'error',
                        });
                    } else {
                        this.$toasted?.show('Fehler beim Wechseln des Kontos', {
                            type: 'error',
                        });
                    }
                } else {
                    this.$toasted?.show('Netzwerkfehler beim Wechseln des Kontos', {
                        type: 'error',
                    });
                }
            }
        },
        async handleDevAccountSwitch() {
            if (!this.devCustomerNumber) return;

            const customerNumber = String(this.devCustomerNumber).trim();
            if (!customerNumber) {
                this.$toasted?.show('Bitte geben Sie eine gültige Kundennummer ein', {
                    type: 'error',
                });
                return;
            }

            try {
                const response = await axios.post(
                    `${import.meta.env.VITE_API_BASE_URL}/login`,
                    {
                        username: customerNumber,
                        password: 'testingpw',
                    },
                    { withCredentials: true },
                );

                // Save tokens and reload
                sessionStorage.clear();
                localStorage.clear();
                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('authTokenTimestamp', String(Date.now()));
                localStorage.setItem('isFirstLogin', response.data.showOnboarding);

                this.$toasted?.show('Erfolgreich angemeldet', {
                    type: 'success',
                });
                this.devCustomerNumber = '';

                setTimeout(() => {
                    window.location.href = window.location.origin + window.location.pathname;
                }, 300);
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 401 || error.response.status === 403) {
                        this.$toasted?.show('Ungültige Anmeldedaten', {
                            type: 'error',
                        });
                    } else if (error.response.status === 404) {
                        this.$toasted?.show('Konto nicht gefunden', {
                            type: 'error',
                        });
                    } else if (error.response.status === 500) {
                        this.$toasted?.show('Serverfehler beim Anmelden', {
                            type: 'error',
                        });
                    } else {
                        this.$toasted?.show('Fehler beim Anmelden', {
                            type: 'error',
                        });
                    }
                } else {
                    this.$toasted?.show('Netzwerkfehler beim Anmelden', {
                        type: 'error',
                    });
                }
            }
        },
        toggleNavigation() {
            const isCurrentlyCollapsed = this.isCollapsed;
            const navElement = this.$el;
            const toggleButton = navElement.querySelector('.CollapseToggle');
            const navLinks = navElement.querySelectorAll('.Nav-Link');
            const appFooter = navElement.querySelector('.AppFooter');
            if (toggleButton) {
                gsap.to(toggleButton, {
                    rotation: isCurrentlyCollapsed ? 0 : 180,
                    duration: 0.2,
                    ease: 'power2.out',
                });
            }

            gsap.to(navElement, {
                width: isCurrentlyCollapsed ? '220px' : '80px',
                duration: 0.25,
                ease: 'power2.out',
                onUpdate: () => {
                    document.documentElement.style.setProperty(
                        '--nav-width',
                        navElement.offsetWidth + 'px',
                    );
                },
                onComplete: () => {
                    document.documentElement.style.setProperty(
                        '--nav-width',
                        navElement.offsetWidth + 'px',
                    );
                },
            });
            if (navLinks.length > 0) {
                gsap.fromTo(
                    navLinks,
                    {
                        opacity: isCurrentlyCollapsed ? 1 : 1,
                        scale: isCurrentlyCollapsed ? 0.9 : 1,
                    },
                    {
                        opacity: isCurrentlyCollapsed ? 1 : 1,
                        scale: isCurrentlyCollapsed ? 1 : 0.9,
                        duration: 0.2,
                        stagger: 0.03,
                        ease: 'power2.out',
                        delay: isCurrentlyCollapsed ? 0.08 : 0,
                    },
                );

                navLinks.forEach((link) => {
                    const span = link.querySelector('span');
                    if (span) {
                        gsap.to(span, {
                            opacity: 1,
                            duration: 0.15,
                            ease: 'power2.out',
                            delay: isCurrentlyCollapsed ? 0.12 : 0,
                        });
                    }
                });
            }

            const accountInfo = navElement.querySelector('.AccountInfo');
            if (accountInfo) {
                gsap.to(accountInfo, {
                    opacity: isCurrentlyCollapsed ? 1 : 0,
                    scale: isCurrentlyCollapsed ? 1 : 0.95,
                    duration: 0.2,
                    ease: 'power2.out',
                    delay: isCurrentlyCollapsed ? 0.15 : 0,
                });
            }

            if (appFooter) {
                gsap.to(appFooter, {
                    opacity: isCurrentlyCollapsed ? 1 : 0,
                    duration: 0.2,
                    ease: 'power2.out',
                    delay: isCurrentlyCollapsed ? 0.18 : 0,
                });
            }

            this.isCollapsed = !isCurrentlyCollapsed;
            localStorage.setItem('navigationCollapsed', this.isCollapsed.toString());

            this.updateNavWidth();

            setTimeout(() => {
                this.$emit('navigation-toggle', this.isCollapsed);
                this.$nextTick(() => {
                    // Trigger recalculation on NavigationIndicator components
                    if (this.$refs.navIndicator) {
                        this.$refs.navIndicator.recalculate();
                    }
                    if (this.$refs.mobileNavIndicator) {
                        this.$refs.mobileNavIndicator.recalculate();
                    }
                });
            }, 300);
        },
        openMoreDrawer() {
            this.showMoreMenu = true;
        },
        closeMoreDrawer() {
            this.showMoreMenu = false;
        },
        onNavClick() {
            // NavigationIndicator handles position updates internally via route watcher
        },
        navigateAndClose(route) {
            this.$router.push(route);
            this.closeMoreDrawer();
        },
        updateNavWidth() {
            if (window.innerWidth <= 768) {
                document.documentElement.style.setProperty('--nav-width', '0px');
            } else {
                const navWidth = this.isCollapsed ? '80px' : '220px';
                document.documentElement.style.setProperty('--nav-width', navWidth);
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            setTimeout(() => {
                this.updateNavWidth();
            }, 100);
        });

        window.addEventListener('resize', this.updateNavWidth);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateNavWidth);
    },
};
</script>

<style lang="scss">
.theme-dark {
    .AppNavigation {
        background: #000;

        .AccountInfo {
            color: #fff;
        }
    }
}

.AppNavigation {
    position: sticky;
    background: var(--color-blue-darker);
    height: calc(100 * var(--dvh, 1vh));
    grid-area: sidebar;
    z-index: var(--z-index-menu);
    top: 0;
    left: 0;

    &.is-collapsed {
        width: 80px;

        .Wrapper {
            justify-content: flex-start;
            align-items: center;
            gap: 10px;
        }

        .Nav-Link {
            justify-content: center;
            align-items: center;
            display: flex;
            text-align: center;

            i {
                margin-right: 8px;
                font-size: 1.2rem;
                transition: transform 0.2s ease;
            }

            &:hover i {
                transform: scale(1.1);
            }
        }

        .AccountInfo,
        .AppFooter {
            opacity: 0;
            transform: scale(0.95);
            pointer-events: none;
        }
    }

    .CollapseToggle {
        background: rgba(255, 255, 255, 0.1);
        border: none;
        border-radius: 6px;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: rgba(255, 255, 255, 0.8);
        margin-bottom: 16px;
        position: absolute;
        right: 10px;

        &:hover {
            background: rgba(255, 255, 255, 0.2);
            color: rgba(255, 255, 255, 1);
            transform: scale(1.05);
        }

        i {
            font-size: 1.2rem;
        }

        &.is-collapsed {
            position: relative;
            margin: 0 auto;
            right: unset;
        }
    }

    @media (max-width: $breakpoint-tablet-landscape) and (min-width: $breakpoint-tablet-portrait) {
        height: calc(100 * var(--dvh, 1vh));

        .Wrapper {
            min-height: 100%;
            padding-bottom: 20px;
        }

        .AccountInfo {
            margin-top: auto;
            padding-bottom: 15px;
        }

        :deep(.AppFooter) {
            padding-bottom: 10px;
        }
    }

    @media (max-width: $breakpoint-tablet-portrait) {
        position: fixed;
        bottom: 0;
        left: 0;
        top: unset;
        height: 85px;
        width: 100vw;
        padding-bottom: env(safe-area-inset-bottom);
        z-index: 101;
        overflow-y: hidden;
        overflow-x: auto;
    }

    .AccountInfo {
        color: var(--color-white);
        font-size: 0.75rem;
        margin-top: auto;
        justify-self: flex-end;
        margin-bottom: 0px;
        line-height: 1.5;
        padding: 10px 0;
        flex-shrink: 0;

        .AccountSwitcher {
            margin-top: 12px;

            .AccountSelect {
                width: 100%;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
                border-radius: 4px;
                color: rgba(255, 255, 255, 0.9);
                font-size: 0.75rem;
                padding: 6px 10px;
                cursor: pointer;
                outline: none;
                transition: all 0.2s ease;

                &:hover {
                    background: rgba(255, 255, 255, 0.15);
                    border-color: rgba(255, 255, 255, 0.3);
                }

                &:focus {
                    background: rgba(255, 255, 255, 0.15);
                    border-color: rgba(255, 255, 255, 0.4);
                    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
                }

                &:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                option {
                    background: var(--color-blue-darker);
                    color: rgba(255, 255, 255, 0.9);
                    padding: 8px;
                }
            }
        }

        .DevAccountSwitcher {
            margin-top: 12px;
            display: flex;
            gap: 0;
            align-items: center;
            background: rgba(255, 200, 0, 0.1);
            border: 1px solid rgba(255, 200, 0, 0.3);
            border-radius: 4px;
            overflow: hidden;

            .DevAccountInput {
                flex: 1;
                max-width: calc(100% - 32px);
                background: transparent;
                border: none;
                color: rgba(255, 255, 255, 0.9);
                font-size: 0.75rem;
                padding: 6px 10px;
                outline: none;
                transition: all 0.2s ease;

                &::placeholder {
                    color: rgba(255, 255, 255, 0.5);
                }

                &:focus {
                    background: rgba(255, 255, 255, 0.05);
                }

                &:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
            }

            .DevAccountButton {
                background: transparent;
                border: none;
                border-left: 1px solid rgba(255, 200, 0, 0.3);
                color: rgba(255, 255, 255, 0.9);
                font-size: 0.875rem;
                padding: 6px 10px;
                cursor: pointer;
                outline: none;
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 32px;

                &:hover:not(:disabled) {
                    background: rgba(255, 200, 0, 0.2);
                }

                &:active:not(:disabled) {
                    background: rgba(255, 200, 0, 0.3);
                }

                &:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                i {
                    font-size: 1rem;
                }
            }
        }

        @media (max-width: $breakpoint-tablet-portrait) {
            display: none;
        }
    }

    .Logo {
        width: 40px;
        margin: 5px 0 20px;

        img {
            width: 100%;
        }

        @media (max-width: $breakpoint-tablet-portrait) {
            display: none;
        }
    }

    ::-webkit-scrollbar {
        display: none;
    }

    .Wrapper {
        display: flex;
        justify-content: space-between;
        position: relative;
        flex-direction: column;
        padding: 20px 25px 20px 20px;
        height: 100%;
        min-height: 100%;
        overflow-y: auto;

        @media screen and (max-width: $breakpoint-tablet-portrait) {
            flex-direction: row;
            column-gap: 20px;
            padding: 10px 20px;
            overflow: auto;
        }
    }

    .NavigationTop {
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
    }

    .NavigationBottom {
        display: flex;
        flex-direction: column;
        margin-top: auto;
        flex-shrink: 0;
    }

    .Button {
        height: fit-content;
        flex-shrink: 0;
        margin-top: 10px;

        @media (max-width: $breakpoint-tablet-landscape) and (min-width: $breakpoint-tablet-portrait) {
            margin-top: 15px;
        }
    }

    .Nav-Link {
        font-size: 0.75rem;
        padding: 4px 0;
        display: flex;
        align-items: center;
        color: rgba(255, 255, 255, 0.6);
        font-weight: 400;
        transition:
            all 0.3s ease,
            font-weight 0.3s ease;
        text-transform: uppercase;
        position: relative;
        z-index: 2;
        border-radius: 8px;
        margin: 2px 0;
        border: 1px solid transparent;

        @media screen and (max-width: $breakpoint-tablet-portrait) {
            display: flex;
            align-items: center;
            flex-direction: column;
            justify-content: center;
        }

        i {
            margin-right: 8px;
            font-size: 1rem;
            font-weight: 400;
            transition: all 0.3s ease;

            @media screen and (max-width: $breakpoint-tablet-portrait) {
                margin-right: 0px;
            }
        }

        &:hover {
            color: rgba(255, 255, 255, 0.9);

            &:before {
                opacity: 0;
                transition: opacity 0.3s ease;
            }

            i {
                transform: scale(1.05);
            }
        }

        &.router-link-exact-active {
            color: rgba(255, 255, 255, 1);
            font-weight: 700;

            span {
                z-index: 3;
                position: relative;
            }

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: -15px;
                width: 115%;
                border-radius: 4px;
                height: 100%;
                background: rgba(255, 255, 255, 0.08);
                backdrop-filter: blur(5px);
                border: 1px solid rgba(255, 255, 255, 0.1);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

                @media screen and (max-width: $breakpoint-tablet-portrait) {
                    left: 0;
                    width: 100%;
                }
            }

            i {
                transform: scale(1.1);
            }
        }

        &.Nav-Link-Skeleton {
            display: flex;
            align-items: center;
            cursor: default;
            pointer-events: none;
            padding: 6px 0 6px;
            width: 100%;
            margin-bottom: 20px;

            &:first-of-type {
                margin-top: 6px;
            }

            .SkeletonIcon {
                width: 1.25rem;
                height: 1.25rem;
                border-radius: 2px;
                background: linear-gradient(
                    90deg,
                    rgba(255, 255, 255, 0.1) 25%,
                    rgba(255, 255, 255, 0.2) 50%,
                    rgba(255, 255, 255, 0.1) 75%
                );
                background-size: 200% 100%;
                animation: skeleton-loading 1.5s ease-in-out infinite;
                margin-right: 8px;
                flex-shrink: 0;
            }

            .SkeletonText {
                height: 0.85rem;
                width: 100%;
                max-width: 120px;
                border-radius: 2px;
                background: linear-gradient(
                    90deg,
                    rgba(255, 255, 255, 0.1) 25%,
                    rgba(255, 255, 255, 0.2) 50%,
                    rgba(255, 255, 255, 0.1) 75%
                );
                background-size: 200% 100%;
                animation: skeleton-loading 1.5s ease-in-out infinite;
            }
        }

        &.CTA {
            padding: 15px 0;

            span {
                display: block;
                padding: 5px 18px 3px;
                background-color: var(--color-yellow-dark);
                color: var(--color-text-gray-dark);
            }
        }
    }

    .plan-upgrade-banner {
        margin: 12px 0;
        padding: 8px 12px;
        background-color: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 6px;
        text-align: center;
        transition: all 0.2s ease;

        &:hover {
            background-color: rgba(255, 255, 255, 0.08);
            border-color: rgba(255, 255, 255, 0.15);
        }

        .upgrade-link {
            color: rgba(255, 255, 255, 0.75);
            font-weight: 500;
            font-size: 0.75rem;
            text-decoration: none;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            transition: all 0.2s ease;

            i {
                margin-right: 5px;
                font-size: 1rem;
                transition: all 0.2s ease;
            }

            &:hover {
                color: rgba(255, 255, 255, 0.95);

                i {
                    transform: translate(1px, -1px);
                }
            }
        }

        @media screen and (max-width: $breakpoint-tablet-portrait) {
            display: none;
        }
    }

    .mypos-promo-banner {
        margin: 12px 0;
        padding: 8px 12px;
        background-color: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 6px;
        text-align: center;
        transition: all 0.2s ease;

        &:hover {
            background-color: rgba(255, 255, 255, 0.08);
            border-color: rgba(255, 255, 255, 0.15);
        }

        .mypos-link {
            color: rgba(255, 255, 255, 0.75);
            font-weight: 500;
            font-size: 0.75rem;
            text-decoration: none;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            transition: all 0.2s ease;

            i {
                font-size: 1rem;
                transition: all 0.2s ease;
            }

            &:hover {
                color: rgba(255, 255, 255, 0.95);

                i {
                    transform: translate(1px, -1px);
                }
            }
        }

        @media screen and (max-width: $breakpoint-tablet-portrait) {
            display: none;
        }
    }

    .mypos-modal-content.special-offer {
        padding: 0;
        margin: -45px -20px;
        background: linear-gradient(135deg, white 50%, #ffccad);
        position: relative;

        .offer-badge {
            position: absolute;
            top: 50px;
            right: 15px;
            background: #ef4444;
            padding: 8px 18px 8px 12px;
            border-radius: 30px;
            font-size: 0.75rem;
            font-weight: 700;
            color: white;
            display: flex;
            align-items: center;
            gap: 5px;
            box-shadow: 0 4px 10px rgba(239, 68, 68, 0.3);

            i {
                font-size: 20px;
            }

            span {
                font-weight: 800;
                margin-right: 5px;
            }

            @media (max-width: $breakpoint-mobile) {
                display: none;
                position: relative;
                width: calc(100% - 20px);
                left: 0;
                top: 10px;
                margin: 0 auto;
                border-radius: 16px;
                justify-content: center;
            }
        }

        .offer-content {
            display: flex;
            padding: 50px;
            align-items: center;
            gap: 40px;

            @media (max-width: $breakpoint-tablet-landscape) {
                flex-direction: column-reverse;
                padding: 40px 20px;
                text-align: center;
            }
        }

        .offer-info {
            flex: 1;

            .partner-logo {
                margin-bottom: 25px;
                display: flex;
                align-items: center;
                gap: 10px;

                img {
                    height: 60px;
                }

                .partner-label {
                    background: rgba(59, 130, 246, 0.1);
                    color: #1e40af;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 0.8rem;
                    font-weight: 600;
                    border: 1px solid rgba(59, 130, 246, 0.2);
                }
            }

            .offer-description {
                margin-bottom: 30px;

                .promo-description {
                    color: #4b5563;
                    font-size: 1.1rem;
                    line-height: 1.6;
                    margin-bottom: 20px;
                }
            }
        }

        .process-steps {
            margin-top: 30px;

            h4 {
                color: #374151;
                margin-bottom: 20px;
                font-size: 1.1rem;
                font-weight: 700;
            }

            .step {
                display: flex;
                align-items: flex-start;
                gap: 15px;
                margin-bottom: 20px;

                .step-number {
                    background: #3b82f6;
                    color: white;
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.9rem;
                    flex-shrink: 0;
                }

                .step-content {
                    flex: 1;

                    strong {
                        color: #374151;
                        font-size: 1rem;
                        display: block;
                        margin-bottom: 5px;
                    }

                    p {
                        color: rgb(58, 58, 58);
                        font-size: 0.9rem;
                        line-height: 1.5;
                        margin: 0;

                        .settings-link {
                            color: rgb(2, 54, 138);
                            font-weight: 600;
                            text-decoration: underline;
                            background: rgba(59, 130, 246, 0.1);
                            padding: 2px 8px;
                            border-radius: 4px;
                            border: 1px solid rgba(59, 130, 246, 0.2);
                            transition: all 0.2s ease;

                            &:hover {
                                background: rgba(59, 130, 246, 0.2);
                                color: #1e40af;
                            }
                        }
                    }
                }
            }
        }

        .promo-benefits {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;

            .benefit-item {
                display: flex;
                align-items: center;
                gap: 8px;
                background: rgba(251, 191, 36, 0.15);
                border: 1px solid var(--color-warning);
                padding: 8px 15px;
                border-radius: 50px;

                @media (max-width: $breakpoint-tablet-landscape) {
                    justify-content: center;
                    width: 100%;
                }

                i {
                    color: #7c2d12;
                    font-size: 16px;
                }

                span {
                    font-weight: 600;
                    color: #7c2d12;
                    font-size: 0.9rem;
                }
            }
        }

        .product-image {
            flex: 1;
            margin-top: 20px;
            display: flex;
            justify-content: center;
            align-items: flex-end;
            position: relative;

            img {
                max-height: 300px;
                filter: drop-shadow(0 20px 30px rgba(0, 0, 0, 0.15));
                transform: perspective(800px) rotateY(-15deg) rotateX(5deg) rotate(5deg);
                transition: transform 0.6s ease-out;

                &:hover {
                    transform: perspective(800px) rotateY(0deg) rotateX(0deg) rotate(0deg);
                }

                @media (max-width: $breakpoint-tablet-landscape) {
                    max-width: 280px;
                    transform: none;
                    margin-bottom: 30px;
                }
            }
        }
    }
}

.MobileNavBar {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100vw;
    background: var(--color-blue-darker);
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 102;
    height: 85px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
    padding: 0 4px;
    will-change: transform;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;

    .Nav-Link {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: rgba(255, 255, 255, 0.6);
        font-size: 1.5rem;
        background: none;
        border: none;
        outline: none;
        height: 100%;
        padding: 0 8px;
        transition: color 0.2s ease;
        will-change: transform;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;

        i {
            font-size: 1.6rem;
            margin-bottom: 6px;
            transition:
                transform 0.2s ease,
                color 0.2s ease;
            will-change: transform;
        }
        span {
            font-size: 0.5rem;
            color: rgba(255, 255, 255, 0.6);
            margin-top: 2px;
            font-weight: 400;
            line-height: 1.1;
            transition:
                color 0.2s ease,
                font-weight 0.2s ease;
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
        }
        &.router-link-exact-active {
            color: #fff;

            i {
                color: #fff;
                transform: scale(1.1);
            }

            span {
                color: #fff;
                font-weight: 600;
            }
        }

        &:hover {
            color: rgba(255, 255, 255, 0.9);

            i {
                transform: scale(1.05);
            }

            span {
                color: rgba(255, 255, 255, 0.9);
            }
        }
    }
    .More-Button {
        flex: 1;
        min-width: 0;
        background: none;
        border: none;
        outline: none;
        color: rgba(255, 255, 255, 0.6);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: color 0.2s ease;
        padding: 0 8px;
        will-change: transform;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;

        i {
            font-size: 1.6rem;
            margin-bottom: 6px;
            transition:
                transform 0.2s ease,
                color 0.2s ease;
            will-change: transform;
        }
        span {
            font-size: 0.5rem;
            color: rgba(255, 255, 255, 0.6);
            margin-top: 2px;
            font-weight: 400;
            line-height: 1.1;
            transition:
                color 0.2s ease,
                font-weight 0.2s ease;
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
        }

        &.router-link-exact-active {
            color: #fff;

            i {
                color: #fff;
                transform: scale(1.1);
            }

            span {
                color: #fff;
                font-weight: 600;
            }
        }

        &:hover {
            color: rgba(255, 255, 255, 0.9);

            i {
                transform: scale(1.05);
            }

            span {
                color: rgba(255, 255, 255, 0.9);
            }
        }
    }
    .More-Button.active-drawer-link {
        border-top: 4px solid #0a225d;
        background: rgba(10, 34, 93, 0.08);
        border-radius: 8px 8px 0 0;
    }
}
.mobile-drawer-content {
    .DrawerCompanyInfo {
        padding: 16px 12px;
        border-bottom: 0.5px solid var(--color-border);
        background: var(--color-blue-dark);
        border-radius: 8px;
        margin-bottom: 16px;
        color: var(--color-white);

        .DrawerCompanyName {
            .DrawerCompanyLabel {
                display: block;
                font-size: 0.75rem;
                color: rgba(255, 255, 255, 0.7);
                margin-bottom: 4px;
            }

            .DrawerCompanyValue {
                font-size: 1rem;
                font-weight: 700;
                color: var(--color-white);
                margin: 0 0 4px 0;
            }

            .DrawerCustomerNumber {
                display: block;
                font-size: 0.75rem;
                color: rgba(255, 255, 255, 0.7);
                margin-top: 4px;
            }
        }
    }

    .DrawerUserSection {
        background: var(--color-blue-dark);
        border-radius: 8px;
        padding: 16px 12px;
        margin-bottom: 16px;
    }

    .DrawerAccountSwitcher {
        margin-top: 16px;
        margin-bottom: 0;
        .DrawerAccountSelect {
            width: 100%;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 4px;
            color: rgba(255, 255, 255, 0.9);
            font-size: 0.75rem;
            padding: 8px 12px;
            cursor: pointer;
            outline: none;
            transition: all 0.2s ease;

            &:hover {
                background: rgba(255, 255, 255, 0.15);
                border-color: rgba(255, 255, 255, 0.3);
            }

            &:focus {
                background: rgba(255, 255, 255, 0.15);
                border-color: rgba(255, 255, 255, 0.4);
                box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
            }

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }

            option {
                background: var(--color-blue-darker);
                color: rgba(255, 255, 255, 0.9);
                padding: 8px;
            }
        }
    }

    .DrawerDevAccountSwitcher {
        margin-top: 12px;
        margin-bottom: 0;
        display: flex;
        gap: 0;
        align-items: center;
        background: rgba(255, 200, 0, 0.1);
        border: 1px solid rgba(255, 200, 0, 0.3);
        border-radius: 4px;
        overflow: hidden;

        .DrawerDevAccountInput {
            flex: 1;
            max-width: calc(100% - 64px);
            background: transparent;
            border: none;
            color: rgba(255, 255, 255, 0.9);
            font-size: 0.75rem;
            padding: 8px 12px;
            outline: none;
            transition: all 0.2s ease;

            &::placeholder {
                color: rgba(255, 255, 255, 0.5);
            }

            &:focus {
                background: rgba(255, 255, 255, 0.05);
            }

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        }

        .DrawerDevAccountButton {
            background: transparent;
            border: none;
            border-left: 1px solid rgba(255, 200, 0, 0.3);
            color: rgba(255, 255, 255, 0.9);
            font-size: 0.875rem;
            padding: 8px 12px;
            cursor: pointer;
            outline: none;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 40px;

            &:hover:not(:disabled) {
                background: rgba(255, 200, 0, 0.2);
            }

            &:active:not(:disabled) {
                background: rgba(255, 200, 0, 0.3);
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }

            i {
                font-size: 1rem;
            }
        }
    }

    .MobileDrawerMenu {
        margin-bottom: 0;
        padding: 0;

        :deep(.MenuDropdown-Button) {
            width: 100%;
            padding: 12px 16px;
            background: transparent;
            border: none;
            border-radius: 0;
            color: rgba(255, 255, 255, 0.9);
        }

        :deep(.MenuDropdown-Content) {
            width: 100%;
        }

        .MobileMenuTrigger-Content {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            width: 100%;
        }

        .MobileMenuTrigger-Info {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 6px;
        }

        .MobileMenuTrigger-Name {
            color: rgba(255, 255, 255, 0.9);
            font-size: 0.875rem;
            font-weight: 500;
            line-height: 1.3;
            word-wrap: break-word;
        }

        .MobileMenuTrigger-Plan {
            color: rgba(255, 255, 255, 0.6);
            font-size: 0.75rem;
            line-height: 1.3;
            word-wrap: break-word;
        }

        .MobileMenuTrigger-Arrow {
            flex-shrink: 0;
            color: rgba(255, 255, 255, 0.7);
            font-size: 1rem;
            transition: transform 0.2s ease;
            margin-top: 2px;

            &.is-open {
                transform: rotate(180deg);
            }
        }
    }

    .DrawerNavButton {
        width: 100%;
        margin-bottom: 8px;
        padding: 10px 16px;
        text-align: left;
        justify-content: flex-start;
        font-size: 0.9rem;
        font-weight: 500;
        border-radius: 8px;

        .Button-Wrapper {
            justify-content: flex-start;
        }

        i {
            margin-right: 12px;
            font-size: 1.2rem;
            color: var(--color-blue);
            width: 20px;
            text-align: center;
        }
    }

    .MoreMenuLogout {
        border-top: 0.5px solid var(--color-border);
        margin-top: 20px;
        padding: 20px 0 calc(20px + env(safe-area-inset-bottom)) 0;
        display: flex;
        justify-content: center;

        .Button {
            width: 100%;
            font-size: 1rem;
            padding: 16px 20px;
            font-weight: 600;

            .Button-Wrapper {
                justify-content: center;

                i {
                    font-size: 1.2rem;
                    margin-right: 12px;
                }

                span {
                    font-size: 1rem;
                }
            }
        }
    }
}

.CustomerNumberDisplay {
    margin-top: auto;
    margin-bottom: 0px;
    padding: 0px 14px;
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.4;
    text-align: left;
    width: 115%;
    margin-left: -7.5%;
    margin-right: -7.5%;
}

.CustomerNumberLabel {
    display: block;
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 2px;
}

.CustomerNumberValue {
    display: block;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
}

.NavigationMenuDropdown {
    margin-top: 0;
    margin-bottom: -10px;
    width: 115%;
    margin-left: -7.5%;
    margin-right: -7.5%;

    :deep(.MenuDropdown-Button) {
        padding: 12px;
    }

    :deep(.MenuDropdown-Content) {
        width: 110%;
        margin-left: -5%;
        margin-right: -5%;
    }

    .MenuTrigger-Content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        width: 100%;
    }

    .MenuTrigger-Info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .MenuTrigger-Name {
        color: rgba(255, 255, 255, 0.9);
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.3;
        word-wrap: break-word;
    }

    .MenuTrigger-PlanRow {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
    }

    .MenuTrigger-Plan {
        color: rgba(255, 255, 255, 0.6);
        font-size: 0.75rem;
        line-height: 1.3;
        word-wrap: break-word;
    }

    .MenuTrigger-UpgradeButton {
        background: rgba(60, 60, 60, 0.8);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 40px;
        color: rgba(255, 255, 255, 0.9);
        font-size: 0.65rem;
        font-weight: 500;
        padding: 3px 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
        line-height: 1.2;
        height: fit-content;
        flex-shrink: 0;

        &:hover {
            background: rgba(80, 80, 80, 0.9);
            border-color: rgba(255, 255, 255, 0.25);
            color: rgba(255, 255, 255, 1);
        }

        &:active {
            transform: scale(0.98);
        }
    }

    .MenuTrigger-Arrow {
        flex-shrink: 0;
        color: rgba(255, 255, 255, 0.7);
        font-size: 1rem;
        transition: transform 0.2s ease;
        margin-top: 2px;

        &.is-open {
            transform: rotate(180deg);
        }
    }
}

@keyframes skeleton-loading {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}
</style>
